import { useReactQuery } from '@flik/shared/api';
import { useTheme } from '@flik/shared/components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavBar from '../components/NavBar';

const ConfirmCreateOrganizationPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiClient = useReactQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');

  // Get the token from URL parameters
  const token = searchParams.get('token');

  // Create the mutation hook for confirming organization creation
  const confirmCreateOrganization = apiClient.useMutation(
    'post',
    '/v1/people/confirm-create-organization',
  );

  // Handle the confirmation on component mount
  useEffect(() => {
    const handleConfirmation = async () => {
      if (!token) {
        setError(
          'Invalid confirmation link. Please check your email for the correct link.',
        );
        setIsLoading(false);
        return;
      }

      try {
        const response = await confirmCreateOrganization.mutateAsync({
          body: {
            org_creation_token: token,
          },
        });

        if (response.organization) {
          setOrganizationName(response.organization.name);
          setIsSuccess(true);
        } else {
          setError(
            'Failed to confirm organization creation. Please try again.',
          );
        }
      } catch (err) {
        console.error('Error confirming organization creation:', err);
        setError(
          'An error occurred while confirming your organization. Please try again or contact support.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    handleConfirmation();
  }, [token]); // Remove confirmCreateOrganization from dependencies

  const handleBackToHome = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Loading spinner */}
            <div className="mx-auto w-16 h-16 mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Confirming your organization...
            </h1>

            <p className={`text-xl ${provideTailwind('text-secondary')}`}>
              Please wait while we set up your organization.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Error icon */}
            <div className="mx-auto w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Something went wrong
            </h1>

            <p className={`text-xl mb-8 ${provideTailwind('text-secondary')}`}>
              {error}
            </p>

            <button
              onClick={handleBackToHome}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${provideTailwind(
                'bg-secondary text-primary hover:bg-opacity-80',
              )}`}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Success tick icon */}
            <div className="mx-auto w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Welcome to Flik, {organizationName}!
            </h1>

            <p className={`text-xl mb-8 ${provideTailwind('text-secondary')}`}>
              Your organization has been successfully created. Check your email
              for next steps to complete your setup.
            </p>

            <button
              onClick={handleBackToHome}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${provideTailwind(
                'bg-secondary text-primary hover:bg-opacity-80',
              )}`}
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ConfirmCreateOrganizationPage;
